import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Group, SegmentedControl, Select, Stack, Tabs, Text, TextInput } from "@mantine/core";
import { Alert, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconBrain, IconCalendarX, IconCurrencyDollar, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { CatalogModel } from "@models/providerCatalog";
import { providerCatalogApi } from "@services/providerCatalogApi";
import { useApiResource } from "@hooks/monitoring/useApiResource";
import { AdminTable, col } from "@components/AdminTable";
import { DeprecationBadge, DeprecationModal, PriceModal } from "@features/Providers";
import ModelUsageTab from "./ModelUsageTab";
import { priceSummary } from "@features/Providers/priceFormat";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { AdminPermissions } from "@config/permissions";

const PAGE_SIZE = 25;
const CAPABILITIES = ["all", "chat", "embedding", "image_generation", "ocr"] as const;
const PRICES = ["all", "admin", "library", "none"] as const;
const STATUSES = ["all", "deprecated"] as const;
const SCOPES = ["in_use", "all"] as const;

/**
 * The models the platform actually uses (metering, last 30 days), prices (price
 * table) or retires (deprecations), with where each price comes from. "All models"
 * adds every model genai-prices prices for the providers in use, so one can be
 * priced or retired before its first call.
 */
export const ModelsPage = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const scope = params.get("scope") === "all" ? "all" : "in_use";
  const catalog = useApiResource(`model-catalog:${scope}`, (signal) =>
    providerCatalogApi().models({ include_library: scope === "all" }, signal));
  const providers = useApiResource("providers", (signal) => providerCatalogApi().catalog(signal));

  const provider = params.get("provider") ?? "";
  const capability = (CAPABILITIES as readonly string[]).includes(params.get("capability") ?? "") ? params.get("capability")! : "all";
  const price = (PRICES as readonly string[]).includes(params.get("price") ?? "") ? params.get("price")! : "all";
  const statusFilter = (STATUSES as readonly string[]).includes(params.get("status") ?? "") ? params.get("status")! : "all";
  const tab = params.get("tab") === "usage" ? "usage" : "catalog";
  const { hasPermission } = useAdminPermissions();
  const canManagePrices = hasPermission(AdminPermissions.PROVIDER_MANAGE);
  const canViewUsage = hasPermission(AdminPermissions.MONITORING_VIEW);
  const [deprecatingKey, setDeprecatingKey] = useState<string | null>(null);
  // A key, not the object: after a price change the catalog reloads and the modal
  // must show the model's new prices.
  const [pricingKey, setPricingKey] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const set = (name: string, value: string | null) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value && value !== "all") next.set(name, value);
      else next.delete(name);
      return next;
    }, { replace: true });

  // "all" is a real choice here (the other filters treat it as no filter): the default is in_use.
  const setScope = (value: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value === "all") next.set("scope", "all");
      else next.delete("scope");
      return next;
    }, { replace: true });

  const labels = useMemo(
    () => Object.fromEntries((providers.data?.providers ?? []).map((p) => [p.key, p.label])),
    [providers.data],
  );
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (catalog.data?.models ?? []).filter((m) =>
      (!provider || m.provider_key === provider) &&
      (capability === "all" || m.capability === capability) &&
      (price === "all" || m.price === price) &&
      (statusFilter === "all" || m.deprecation !== null) &&
      (!needle || m.model_id.toLowerCase().includes(needle)));
  }, [catalog.data, provider, capability, price, statusFilter, search]);
  useEffect(() => setPage(1), [scope, provider, capability, price, statusFilter, search]);
  const keyOf = (m: CatalogModel) => `${m.provider_key}|${m.capability}|${m.model_id}`;
  const listedModels = catalog.data?.models ?? [];
  const pricing = listedModels.find((m) => keyOf(m) === pricingKey) ?? null;
  const deprecating = listedModels.find((m) => keyOf(m) === deprecatingKey) ?? null;
  const deprecationOf = (providerKey: string, modelId: string) =>
    (catalog.data?.models ?? []).find((m) => m.provider_key === providerKey && m.model_id === modelId)?.deprecation ?? null;
  const replacementsFor = (m: CatalogModel) => (catalog.data?.models ?? [])
    .filter((x) => x.provider_key === m.provider_key && x.capability === m.capability && !x.deprecation)
    .map((x) => x.model_id);

  const changed = () => catalog.reload();

  const columns = [
    col.twoLine<CatalogModel>("model", t("admin.models.columns.model"), (m) => ({
      primary: m.model_id,
      secondary: m.source === "metering"
        ? t("admin.models.calls", { count: m.event_count })
        : t(`admin.models.source.${m.source}`),
    }), { mono: true }),
    col.text<CatalogModel>("provider", t("admin.models.columns.provider"), (m) => labels[m.provider_key] ?? m.provider_key,
                           { width: "18%" }),
    col.badge<CatalogModel>("capability", t("admin.models.columns.capability"),
      (m) => <Badge size="sm" variant="light">{t(`admin.models.capability.${m.capability}`, { defaultValue: m.capability })}</Badge>, 152),
    col.date<CatalogModel>("last", t("admin.models.columns.lastUsed"), (m) => m.last_used),
    col.badge<CatalogModel>("status", t("admin.models.columns.status"), (m) =>
      m.deprecation ? <DeprecationBadge deprecation={m.deprecation} /> : <Text size="sm" c="dimmed">-</Text>, 176),
    col.twoLine<CatalogModel>("price", t("admin.models.columns.price"), (m) => ({
      primary: priceSummary(m) ?? t("admin.models.price.none"),
      secondary: m.price === "none" ? t("admin.models.prices.unpricedShort") : t(`admin.models.price.${m.price}`),
    }), { width: 240 }),
    col.badge<CatalogModel>("credential", t("admin.models.columns.credential"), (m) =>
      m.credential_configured === null ? <Text size="sm" c="dimmed">-</Text> : (
        <Badge size="sm" variant="light" color={m.credential_configured ? "green" : "gray"}>
          {t(m.credential_configured ? "admin.models.credential.yes" : "admin.models.credential.no")}
        </Badge>
      ), 140),
    col.actions<CatalogModel>("", (m) => [{
      label: t(canManagePrices ? "admin.models.prices.set" : "admin.models.prices.view"),
      icon: <IconCurrencyDollar size={16} />,
      color: m.price === "none" ? "orange" : undefined,
      // Without manage rights only an admin price is worth opening.
      hidden: !canManagePrices && m.price !== "admin",
      onClick: () => setPricingKey(keyOf(m)),
    }, {
      label: t(m.deprecation ? "admin.models.deprecation.edit" : "admin.models.deprecation.mark"),
      icon: <IconCalendarX size={16} />,
      color: m.deprecation ? "orange" : undefined,
      hidden: !canManagePrices,
      onClick: () => setDeprecatingKey(keyOf(m)),
    }]),
  ];

  return (
    <Stack gap="md">
      <PageHeader title={t("admin.models.title")} description={t("admin.models.description")} icon={<IconBrain size={28} />} />
      {catalog.error && <Alert color={catalog.forbidden ? "gray" : "red"} title={t("admin.models.loadFailed")}>{catalog.error}</Alert>}
      {canViewUsage && (
        <Tabs value={tab} onChange={(value) => set("tab", value === "usage" ? "usage" : null)}>
          <Tabs.List>
            <Tabs.Tab value="catalog">{t("admin.models.tabs.catalog")}</Tabs.Tab>
            <Tabs.Tab value="usage">{t("admin.models.tabs.usage")}</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      )}
      {tab === "usage" && canViewUsage ? (
        <SectionCard title={t("admin.models.usage.title")}>
          <ModelUsageTab labels={labels} deprecationOf={deprecationOf} />
        </SectionCard>
      ) : (
      <SectionCard
        title={
          <Group gap="xs">
            <Text fw={600}>{t("admin.models.list")}</Text>
            <Text size="sm" c="dimmed">{t("admin.models.count", { count: rows.length })}</Text>
          </Group>
        }
        actions={catalog.data && (
          <Text size="xs" c="dimmed">
            {t("admin.models.versions", { days: catalog.data.window_days, prices: catalog.data.prices_version })}
          </Text>
        )}
      >
        <Stack gap="md">
          <Group gap="sm" wrap="wrap">
            <SegmentedControl
              size="sm"
              value={scope}
              onChange={setScope}
              data={SCOPES.map((value) => ({ value, label: t(`admin.models.scope.${value}`) }))}
              aria-label={t("admin.models.scope.label")}
            />
            <TextInput
              size="sm"
              w={260}
              leftSection={<IconSearch size={16} />}
              placeholder={t("admin.models.search")}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              aria-label={t("admin.models.search")}
            />
            <Select
              size="sm"
              w={220}
              clearable
              placeholder={t("admin.models.allProviders")}
              data={(providers.data?.providers ?? []).filter((p) => p.supported).map((p) => ({ value: p.key, label: p.label }))}
              value={provider || null}
              onChange={(value) => set("provider", value)}
              aria-label={t("admin.models.columns.provider")}
            />
            <SegmentedControl
              size="sm"
              value={capability}
              onChange={(value) => set("capability", value)}
              data={CAPABILITIES.map((value) => ({ value, label: t(`admin.models.capability.${value}`) }))}
            />
            <Select
              size="sm"
              w={180}
              allowDeselect={false}
              value={statusFilter}
              onChange={(value) => set("status", value)}
              data={STATUSES.map((value) => ({ value, label: t(`admin.models.status.${value}`) }))}
              aria-label={t("admin.models.columns.status")}
            />
            <Select
              size="sm"
              w={180}
              allowDeselect={false}
              value={price}
              onChange={(value) => set("price", value)}
              data={PRICES.map((value) => ({ value, label: t(`admin.models.price.${value}`) }))}
              aria-label={t("admin.models.columns.price")}
            />
          </Group>
          <AdminTable
            columns={columns}
            data={rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
            getRowKey={keyOf}
            loading={catalog.loading}
            emptyMessage={t("admin.models.empty")}
            pagination={rows.length > PAGE_SIZE ? { page, total: rows.length, pageSize: PAGE_SIZE, onChange: setPage } : undefined}
          />
          <Text size="xs" c="dimmed">{t("admin.models.footnote")}</Text>
        </Stack>
      </SectionCard>
      )}
      <DeprecationModal
        model={deprecating}
        providerLabel={deprecating ? labels[deprecating.provider_key] : undefined}
        replacements={deprecating ? replacementsFor(deprecating) : []}
        onClose={() => setDeprecatingKey(null)}
        onChanged={changed}
      />
      <PriceModal
        model={pricing}
        providerLabel={pricing ? labels[pricing.provider_key] : undefined}
        canManage={canManagePrices}
        onClose={() => setPricingKey(null)}
        onChanged={changed}
      />
    </Stack>
  );
};
